import React, { useState, useContext } from 'react';
import {
  TextField, Button, RadioGroup, FormControlLabel, Radio, FormControl, Box, Typography, Paper, InputAdornment, Grid, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { AccountCircle, Subject, Message } from '@mui/icons-material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { ContextSession } from '../../../services/ContextSession';

const customTheme = (outerTheme) =>
  createTheme({
    palette: {
      mode: outerTheme.palette.mode,
    },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif',
      h4: {
        fontWeight: '600',
        fontSize: '1.8rem',
        color: '#333',
      },
    },
  });

const EnviarMensaje = () => {
  const [remitente, setRemitente] = useState('Comunicaciones INJUPEMP');
  const [asunto, setAsunto] = useState('');
  const [contenido, setContenido] = useState('');
  const [categoria, setCategoria] = useState('');
  const [identidades, setIdentidades] = useState('');
  const [showCustomField, setShowCustomField] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const sessi = useContext(ContextSession);
  const outerTheme = useTheme();

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogMessage('');
  };

  const handleEnviarMensaje = async () => {
    if (!asunto || !contenido || !categoria || (categoria === 'Personalizado' && !identidades)) {
      setDialogMessage('Por favor, complete todos los campos del formulario.');
      setOpenDialog(true);
      return;
    }

    const usuariosPersonalizados = categoria === 'Personalizado'
      ? identidades.split('\n').filter(id => id.trim() !== '')
      : [];

    const data = {
      remitente,  // Usar el valor ingresado en el campo "Remitente"
      asunto,
      contenido,
      tipoUsuario: categoria,
      usuarioCreadorId: sessi.sessionData.id,
      usuariosPersonalizados
    };

    try {
      const response = await fetch("https://be.injupemp.gob.hn:8443/api/buzonPart/enviarMensaje/", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessi.sessionData.token}`
        },
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      if (response.ok) {
        setDialogMessage('Mensaje enviado con éxito');
        setRemitente('Comunicaciones INJUPEMP'); // Restablecer al valor inicial si deseas
        setAsunto('');
        setContenido('');
        setCategoria('');
        setIdentidades('');
      } else {
        const errorMessage = responseData.error || 'Error desconocido';
        setDialogMessage(`Error al enviar el mensaje: ${errorMessage}`);
      }
    } catch (error) {
      setDialogMessage(`Error de conexión: ${error.message}`);
    } finally {
      setOpenDialog(true);
    }
  };


  const handleCategoriaChange = (e) => {
    setCategoria(e.target.value);
    setShowCustomField(e.target.value === 'Personalizado');
  };

  const handleIdentidadesChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    const formattedValue = value.replace(/(\d{13})(?=\d)/g, '$1\n');
    setIdentidades(formattedValue.trim());
  };

  return (
    <ThemeProvider theme={customTheme(outerTheme)}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Grid container spacing={2} justifyContent="center" alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Paper elevation={6} sx={{ p: 4, borderRadius: '16px' }}>
              <Typography variant="h4" align="center" sx={{ mb: 3 }}>Enviar Mensaje</Typography>
              <TextField
                label="Remitente"
                variant="outlined"
                fullWidth
                value={remitente}
                onChange={(e) => {
                  //console.log("Nuevo valor de remitente:", e.target.value);
                  setRemitente(e.target.value);
                }}
                
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Asunto"
                variant="outlined"
                fullWidth
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Subject sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Contenido"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={contenido}
                onChange={(e) => setContenido(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Message sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>Enviar Mensaje a:</Typography>
                <RadioGroup value={categoria} onChange={handleCategoriaChange}>
                  <FormControlLabel value="Todos" control={<Radio />} label="Todos" />
                  <FormControlLabel value="Jubilados" control={<Radio />} label="Jubilados" />
                  <FormControlLabel value="Pensionados" control={<Radio />} label="Pensionados" />
                  <FormControlLabel value="Activos" control={<Radio />} label="Activos" />
                  <FormControlLabel value="Personalizado" control={<Radio />} label="Personalizado" />
                </RadioGroup>
              </FormControl>
              {showCustomField && (
                <TextField
                  label="Ingresar números de identidad (uno por línea)"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={8}
                  value={identidades}
                  onChange={handleIdentidadesChange}
                  sx={{ mb: 3 }}
                />
              )}
              <Box display="flex" justifyContent="center">
                <Button
                  onClick={handleEnviarMensaje}
                  variant="contained"
                  sx={{
                    backgroundColor: '#89CFE0',
                    color: '#fff',
                    py: 1.5,
                    px: 4,
                    ':hover': {
                      backgroundColor: '#72D2DA',
                    },
                  }}
                >
                  Enviar Mensaje
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Mensaje</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default EnviarMensaje;
